/**
 * Tripnovac Visa — About Us
 * Enterprise mission, stats, features, and values
 *
 * @company Alpha Libra LLC
 * @domain tripnovac-visa.com
 */

import React from 'react';
import {
  Box, Container, Typography, Grid, Card, Paper, Avatar,
  Button, Divider, useTheme, alpha, Chip,
} from '@mui/material';
import {
  Public, FlightTakeoff, Shield, Groups, SmartToy, Security,
  TrackChanges, ManageAccounts, TrendingUp, Favorite, Visibility,
  Handshake, ArrowForward, Verified,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '../../constants';

// =====================================================
// STATIC DATA
// =====================================================

const STATS = [
  { label: 'Country Records', value: '250', icon: <Public />, color: '#2563EB' },
  { label: 'Application Records', value: 'Persistent', icon: <FlightTakeoff />, color: '#7C3AED' },
  { label: 'Access Model', value: 'Role-Based', icon: <TrendingUp />, color: '#059669' },
  { label: 'Support', value: 'Ticketed', icon: <Groups />, color: '#D97706' },
];

const FEATURES = [
  {
    icon: <SmartToy sx={{ fontSize: 36 }} />,
    title: 'Guided Preparation',
    description:
      'Use country information, document checklists, saved application records, and interview-preparation tools in one account.',
    color: '#2563EB',
  },
  {
    icon: <Security sx={{ fontSize: 36 }} />,
    title: 'Protected Workflows',
    description:
      'Production traffic uses HTTPS, protected routes enforce role access, Stripe handles card details, and configured document storage uses time-limited links.',
    color: '#059669',
  },
  {
    icon: <TrackChanges sx={{ fontSize: 36 }} />,
    title: 'Application Tracking',
    description:
      'Review the status and history recorded in your Tripnovac workflow. Government-authority status and decisions remain external.',
    color: '#7C3AED',
  },
  {
    icon: <ManageAccounts sx={{ fontSize: 36 }} />,
    title: 'Multi-Role Platform',
    description:
      'Account types support individual, agent, corporate, enterprise, and administrative workflows with role-restricted routes.',
    color: '#D97706',
  },
];

const VALUES = [
  {
    icon: <Visibility />,
    title: 'Transparency',
    description: 'Clear pricing, honest timelines, and open communication at every step.',
  },
  {
    icon: <Verified />,
    title: 'Accuracy',
    description: 'Visa records can include source evidence and update history; users should verify current requirements with the relevant authority.',
  },
  {
    icon: <Favorite />,
    title: 'User-First',
    description: 'Every feature is designed around real traveler and enterprise needs.',
  },
  {
    icon: <Handshake />,
    title: 'Trust',
    description: 'Clear descriptions of implemented safeguards without unsupported certification claims.',
  },
];

// =====================================================
// COMPONENT
// =====================================================

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Hero Section ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1E3A5F 0%, #0F2744 60%, #0A1929 100%)',
          color: '#fff',
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 60%, rgba(37,99,235,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(124,58,237,0.10) 0%, transparent 50%)',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Chip
            label="About Tripnovac"
            sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600 }}
          />
          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ lineHeight: 1.2 }}>
            Empowering Global Mobility
            <br />
            Through Intelligent Visa Processing
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, maxWidth: 640, mx: 'auto', mt: 2 }}>
            We believe crossing borders should be simple. {APP_NAME} combines cutting-edge technology with
            comprehensive immigration data to streamline every visa journey.
          </Typography>
        </Container>
      </Box>

      {/* ── Stats Section ── */}
      <Container maxWidth="lg" sx={{ mt: -5, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {STATS.map((stat) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card
                elevation={4}
                sx={{
                  textAlign: 'center',
                  py: 4,
                  borderRadius: 4,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(stat.color, 0.1),
                    color: stat.color,
                    width: 52,
                    height: 52,
                    mx: 'auto',
                    mb: 1.5,
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Typography variant="h4" fontWeight={800}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {stat.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── About / Mission Section ── */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Shield sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Our Mission
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 2, maxWidth: 720, mx: 'auto', fontSize: '1.05rem' }}
        >
          Tripnovac is an enterprise visa processing platform by Alpha Libra LLC, purpose-built to
          simplify the complexity of global immigration. We aggregate visa policies from 249 countries and territories (ISO 3166-1),
          offer AI-powered application assistance, and provide real-time tracking — so travelers,
          agencies, and enterprises can focus on what matters most: the journey ahead.
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 2, maxWidth: 720, mx: 'auto', mt: 3, fontSize: '1.05rem' }}
        >
          From solo adventurers securing their first tourist visa to multinational corporations
          relocating teams across continents, {APP_NAME} delivers the data accuracy, security, and
          scale that modern global mobility demands.
        </Typography>
      </Container>

      {/* ── Features Section ── */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
            Built for the Modern Enterprise
          </Typography>
          <Typography color="text.secondary" textAlign="center" sx={{ mb: 6, maxWidth: 520, mx: 'auto' }}>
            Every capability is designed for reliability, compliance, and scale.
          </Typography>

          <Grid container spacing={4}>
            {FEATURES.map((feat) => (
              <Grid item xs={12} sm={6} key={feat.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: feat.color, boxShadow: 4 },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(feat.color, 0.1),
                      color: feat.color,
                      width: 60,
                      height: 60,
                      mb: 2,
                    }}
                  >
                    {feat.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {feat.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {feat.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Values Section ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
          What We Stand For
        </Typography>
        <Typography color="text.secondary" textAlign="center" sx={{ mb: 6, maxWidth: 480, mx: 'auto' }}>
          Our core values guide every decision we make.
        </Typography>

        <Grid container spacing={4}>
          {VALUES.map((val) => (
            <Grid item xs={12} sm={6} md={3} key={val.title}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {val.icon}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {val.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {val.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── CTA Section ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1E3A5F 0%, #0F2744 100%)',
          color: '#fff',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Ready to get started?
          </Typography>
          <Typography sx={{ opacity: 0.8, mb: 4 }}>
            Explore {APP_NAME} for structured visa research, preparation, and account workflows.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              endIcon={<ArrowForward />}
              sx={{
                px: 5,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                bgcolor: '#fff',
                color: '#1E3A5F',
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
            >
              Create Free Account
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                px: 5,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                borderColor: 'rgba(255,255,255,0.4)',
                color: '#fff',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Contact Sales
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Footer Note ── */}
      <Divider />
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} {APP_NAME} by Alpha Libra LLC. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default AboutPage;
